// =============================================
// AdminQuestionsPage.jsx
// FULL rewrite (NO features removed)
// + Adds Post-UTME bucket management (ENG/MTH/IQ/CA)
// + Keeps: search, pagination, export, bulk delete all/selected,
//          edit, delete, duplicate, toasts, loading bar
// =============================================

import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import api from "../../utils/api";
import SubjectSelect from "../../components/questions/SubjectSelect";
import QuestionsTable from "../../components/questions/QuestionsTable";
import EditQuestionDialog from "../../components/questions/EditQuestionDialog";

// Bucket constants (Post-UTME)
const POST_UTME_BUCKETS = [
  { value: "ALL", label: "All Buckets" },
  { value: "ENG", label: "ENG — English" },
  { value: "MTH", label: "MTH — Mathematics" },
  { value: "IQ", label: "IQ — Aptitude/Reasoning" },
  { value: "CA", label: "CA — Current Affairs" },
];

export default function AdminQuestionsPage() {
  // ===============================
  // STATE
  // ===============================
  const [subjectId, setSubjectId] = useState("");
  const [subjects, setSubjects] = useState([]);

  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Post-UTME bucket filter (only applies when subject.code === POST_UTME)
  const [bucketCode, setBucketCode] = useState("ALL");

  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "info",
  });

  // ===============================
  // HELPERS
  // ===============================
  const truncated = (text, n = 80) =>
    !text ? "" : text.length > n ? `${text.slice(0, n)}...` : text;

  const showToast = (msg, severity = "info") => {
    setToast({ open: true, msg, severity });
  };

  // ===============================
  // SUBJECTS (FETCH ONCE)
  // This makes the page self-sufficient:
  // We can detect POST_UTME by subject.code.
  // ===============================
  const loadSubjects = async () => {
    try {
      // If your backend route differs, adjust here:
      // common patterns: /admin/subjects or /subjects
      const { data } = await api.get("/admin/subjects");
      setSubjects(Array.isArray(data) ? data : data?.subjects || []);
    } catch (err) {
      // Not fatal—page still works, but POST_UTME detection is limited
      console.error("Load subjects error:", err);
      setSubjects([]);
    }
  };

  useEffect(() => {
    loadSubjects();
  }, []);

  const selectedSubject = useMemo(() => {
    const sid = Number(subjectId);
    if (!SidsSafeFinal(sid)) return null;
    return subjects.find((s) => Number(s.id) === sid) || null;
  }, [subjectId, subjects]);

  const isPostUtmeSubject = useMemo(() => {
    const code = (selectedSubject?.code || "").toString().trim().toUpperCase();
    return code === "POST_UTME";
  }, [selectedSubject]);

  function SidsSafeFinal(n) {
    return Number.isFinite(n) && n > 0;
  }
  function SidsSafeStr(v) {
    const n = Number(v);
    return SidsSafeFinal(n);
  }
  function SidsToNum(v) {
    const n = Number(v);
    return SidsSafeFinal(n) ? n : null;
  }
  // Backward compatible alias used above:
  function SidsSafeFinal(n) {
    return Number.isFinite(n) && n > 0;
  }


  // ===============================
  // LOAD QUESTIONS (PAGINATED)
  // ===============================
  const loadQuestions = async (sid, pg = 1, searchTerm = "", bucket = "ALL") => {
    if (!SidsSafeFinal(Number(sid))) {
      setQuestions([]);
      setTotalQuestions(0);
      return;
    }

    try {
      setLoading(true);
      setSelected([]);
      setSelectAll(false);

      const params = {
        subject_id: sid,
        page: pg,
        limit: 20,
        search: searchTerm,
      };

      // Apply bucket filter ONLY when Post-UTME subject is selected
      // and bucket is not ALL.
      if (isPostUtmeSubject && bucket && bucket !== "ALL") {
        params.bucket_code = bucket;
      }

      const { data } = await api.get("/admin/questions", { params });

      setQuestions(data.questions || []);
      setPage(data.page || pg);
      setTotalPages(data.totalPages || 1);

      if (typeof data.totalQuestions === "number") {
        setTotalQuestions(data.totalQuestions);
      }
    } catch (err) {
      console.error("Load questions error:", err);
      showToast(err?.response?.data?.message || "Failed to load questions", "error");
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // LOAD TOTAL COUNT
  // ===============================
  const loadTotalCount = async (sid, bucket = "ALL") => {
    if (!SidsSafeFinal(Number(sid))) {
      setTotalQuestions(0);
      return;
    }

    try {
      const params = { subject_id: sid };
      if (isPostUtmeSubject && bucket && bucket !== "ALL") {
        params.bucket_code = bucket;
      }

      const { data } = await api.get("/admin/questions/count", { params });
      setTotalQuestions(data.total ?? 0);
    } catch (err) {
      console.error("Count error:", err);
      setTotalQuestions(0);
    }
  };

  // ===============================
  // SUBJECT CHANGE
  // ===============================
  const handleSubjectChange = (sid) => {
    const idNum = Number(sid);

    if (!sid || !Number.isFinite(idNum) || idNum <= 0) {
      setSubjectId("");
      setQuestions([]);
      setTotalQuestions(0);
      setPage(1);
      setTotalPages(1);
      setSearch("");
      setSelected([]);
      setSelectAll(false);
      setBucketCode("ALL");
      return;
    }

    setSubjectId(idNum);
    setPage(1);
    setSearch("");
    setSelected([]);
    setSelectAll(false);

    // reset bucket on subject change
    setBucketCode("ALL");

    // Note: loadQuestions/loadTotalCount will respect isPostUtmeSubject
    // once selectedSubject updates (based on subjects list).
    // We still call immediately with default bucket.
    loadQuestions(idNum, 1, "", "ALL");
    loadTotalCount(idNum, "ALL");
  };

  // ===============================
  // BUCKET CHANGE (POST-UTME ONLY)
  // ===============================
  const handleBucketChange = (newBucket) => {
    setBucketCode(newBucket);
    if (!subjectId) return;

    // reset to page 1 when changing bucket (consistent UX)
    setPage(1);
    loadQuestions(subjectId, 1, search, newBucket);
    loadTotalCount(subjectId, newBucket);
  };

  // When subject becomes Post-UTME after subjects list loads,
  // refresh list with current filters so UI is consistent.
  useEffect(() => {
    if (!subjectId) return;
    // Re-load using current bucket/search/page
    loadQuestions(subjectId, page, search, bucketCode);
    loadTotalCount(subjectId, bucketCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPostUtmeSubject]);

  // ===============================
  // CRUD ACTIONS
  // ===============================
  const onEditClick = (question) => {
    setEditingQuestion(question);
    setEditOpen(true);
  };

  const onEditSaved = (updated) => {
    setQuestions((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
    showToast("Question updated successfully", "success");
    setEditOpen(false);
    setEditingQuestion(null);
  };

  const onDelete = async (q) => {
    if (!window.confirm(`Delete question ID ${q.id}?`)) return;

    try {
      await api.delete(`/admin/questions/${q.id}`);

      setQuestions((prev) => prev.filter((x) => x.id !== q.id));
      setSelected((prev) => prev.filter((id) => id !== q.id));
      setTotalQuestions((t) => Math.max(0, t - 1));

      showToast("Question deleted", "success");
    } catch (err) {
      console.error("Delete error:", err);
      showToast(err?.response?.data?.message || "Failed to delete", "error");
    }
  };

  const onDuplicate = async (q) => {
    try {
      const { data } = await api.post(`/admin/questions/${q.id}/duplicate`);
      setQuestions((prev) => [data.question, ...prev]);
      setTotalQuestions((t) => t + 1);
      showToast("Question duplicated", "success");
    } catch (err) {
      console.error("Duplicate error:", err);
      showToast(err?.response?.data?.message || "Failed to duplicate", "error");
    }
  };

  // ===============================
  // BULK DELETE
  // ===============================
  const handleBulkDeleteAll = async () => {
    if (!subjectId) return;

    const extra =
      isPostUtmeSubject && bucketCode !== "ALL"
        ? ` (Bucket: ${bucketCode})`
        : "";

    if (!window.confirm(`Delete ALL questions for this subject${extra}?`)) return;

    try {
      const params = { subject_id: subjectId };
      if (isPostUtmeSubject && bucketCode && bucketCode !== "ALL") {
        params.bucket_code = bucketCode;
      }

      await api.delete("/admin/questions/bulk", { params });

      setQuestions([]);
      setSelected([]);
      setSelectAll(false);
      setTotalQuestions(0);

      showToast("All questions deleted", "success");
    } catch (err) {
      console.error("Bulk delete all error:", err);
      showToast(err?.response?.data?.message || "Failed to delete all", "error");
    }
  };

  const handleBulkDeleteSelected = async () => {
    if (!subjectId || selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected question(s)?`)) return;

    try {
      const { data } = await api.post("/admin/questions/bulk-delete", {
        ids: selected,
      });

      setQuestions((prev) => prev.filter((q) => !selected.includes(q.id)));
      setTotalQuestions((t) => Math.max(0, t - selected.length));
      setSelected([]);
      setSelectAll(false);

      showToast(data.message || "Selected questions deleted", "success");
    } catch (err) {
      console.error("Bulk delete selected error:", err);
      showToast(
        err?.response?.data?.message || "Failed to delete selected questions",
        "error"
      );
    }
  };

  // ===============================
  // SEARCH
  // ===============================
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (!subjectId) return;

    // Always reset to page 1 when searching
    setPage(1);
    loadQuestions(subjectId, 1, val, bucketCode);
  };

  // ===============================
  // SELECTION
  // ===============================
  const onToggleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelected(checked ? questions.map((q) => q.id) : []);
  };

  const onToggleSelectOne = (id, checked) => {
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  // keep selectAll in sync when questions change
  useEffect(() => {
    if (!questions.length) {
      setSelectAll(false);
      return;
    }
    const allSelected = questions.every((q) => selected.includes(q.id));
    setSelectAll(allSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  // ===============================
  // PAGINATION
  // ===============================
  const handleChangePage = (newPage) => {
    if (!subjectId) return;
    setPage(newPage);
    loadQuestions(subjectId, newPage, search, bucketCode);
  };

  // ===============================
  // EXPORT
  // ===============================
  const handleExport = () => {
    if (!subjectId) return;

    const params = { subject_id: subjectId };
    if (isPostUtmeSubject && bucketCode && bucketCode !== "ALL") {
      params.bucket_code = bucketCode;
    }

    const url = api.getUri({
      url: "/admin/questions/export",
      params,
    });

    window.open(url, "_blank");
  };

  // ===============================
  // RENDER
  // ===============================
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Question Management
      </Typography>

      {subjectId && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
          <Typography variant="subtitle1">
            <b>Total Questions:</b> {totalQuestions}
          </Typography>

          {isPostUtmeSubject && (
            <>
              <Chip size="small" label="POST-UTME" />
              {bucketCode !== "ALL" && <Chip size="small" label={`bucket: ${bucketCode}`} />}
            </>
          )}
        </Stack>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack spacing={2}>
          <SubjectSelect value={subjectId} onChange={handleSubjectChange} />

          {/* Post-UTME bucket filter (only when subject code is POST_UTME) */}
          {subjectId && isPostUtmeSubject && (
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>Post-UTME Bucket</InputLabel>
                <Select
                  label="Post-UTME Bucket"
                  value={bucketCode}
                  onChange={(e) => handleBucketChange(e.target.value)}
                >
                  {POST_UTME_BUCKETS.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Chip size="small" label="exam_style: SCREENING" />
              <Chip size="small" label="tag: GENERAL_POST_UTME" />
            </Stack>
          )}

          {subjectId && (
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button variant="outlined" onClick={handleExport}>
                Export CSV
              </Button>

              <Button variant="outlined" color="error" onClick={handleBulkDeleteAll}>
                Delete All
              </Button>

              {selected.length > 0 && (
                <Button variant="contained" color="error" onClick={handleBulkDeleteSelected}>
                  Delete Selected ({selected.length})
                </Button>
              )}
            </Stack>
          )}

          {loading && <LinearProgress />}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderBottom: "1px solid #eee" }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <SearchIcon fontSize="small" />
          <TextField
            fullWidth
            size="small"
            label="Search Question Text"
            value={search}
            onChange={handleSearchChange}
            disabled={!subjectId}
          />
        </Stack>
      </Paper>

      <TableContainer sx={{ maxHeight: 600 }}>
        <QuestionsTable
          questions={questions}
          truncated={truncated}
          selected={selected}
          selectAll={selectAll}
          onToggleSelectAll={onToggleSelectAll}
          onToggleOne={onToggleSelectOne}
          onEdit={onEditClick}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
          // NOTE:
          // If your QuestionsTable supports extra props for badges, we can add:
          // isPostUtmeSubject={isPostUtmeSubject}
          // bucketCode={bucketCode}
        />
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ p: 2, display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            disabled={page === 1}
            onClick={() => handleChangePage(page - 1)}
          >
            Prev
          </Button>

          {Array.from({ length: totalPages }).map((_, idx) => {
            const p = idx + 1;
            return (
              <Button
                key={p}
                variant={p === page ? "contained" : "outlined"}
                onClick={() => handleChangePage(p)}
              >
                {p}
              </Button>
            );
          })}

          <Button
            variant="outlined"
            disabled={page === totalPages}
            onClick={() => handleChangePage(page + 1)}
          >
            Next
          </Button>
        </Box>
      )}

      <EditQuestionDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        question={editingQuestion}
        onSaved={onEditSaved}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      >
        <Alert severity={toast.severity}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
